import Vue from 'vue';
import axios from 'axios';
import storage from '@SHARED/js/helpers/storage';
import cookie from '@SHARED/js/helpers/cookie';
import { validateNumber } from '@SHARED/js/helpers/misc';
import collect from 'collect.js';
import ProductCartItem from './ProductCartItem';

/**
 * This object is the one source of truth for what's in the cart,
 * and works as an interface for localStorage cart data.
 * Only use methods to modify cart data.
 */
const store = new Vue({
  data: {
    cart: {
      items: [],
      preferredShippingMethod: null,
      preferredPaymentMethod: null,
      orderComment: '',
      customerOrderNumber: '',
      session: {},
    },
    info: {}, // Temp. data (e.g. delivery address) not saved to localStorage
    orderData: {}, // Copy of data from backend for finalizing an order (do not save to localStorage!) read-only
  },

  created() {
    this.cart = this.getLocalStorage();

    // Listen to changes to localStorage if other browser tabs are open
    window.addEventListener('storage', () => {
      this.cart = this.getLocalStorage();
    });
  },

  watch: {
    cart: {
      handler() {
        this.writeToLocalStorage();
        this.$emit('cart-updated', this.cart);
      },
      deep: true,
    },
  },

  computed: {
    authUserFullname() {
      if (this.info.authUser) {
        return `${this.info.authUser.firstname} ${this.info.authUser.lastname}`;
      }

      return '';
    },

    customerData() {
      return this.info.authUser.customer;
    },

    customerInvoiceData() {
      return collect(this.info.authUser.customer)
        .only([
          'invoice_address_name',
          'invoice_address1',
          'invoice_address2',
          'invoice_postal_code',
          'invoice_post_office',
          'invoice_country',
          'invoice_email',
        ])
        .all();
    },

    customerDeliveryData() {
      return collect(this.info.authUser.customer)
        .only([
          'delivery_address_name',
          'delivery_address1',
          'delivery_address2',
          'delivery_postal_code',
          'delivery_post_office',
          'delivery_country',
        ])
        .all();
    },

    itemsTotalQuantity() {
      return collect(this.cart.items).sum(item => item.quantity);
    },
  },

  methods: {

    // Cart methods

    addToCart(item) {
      // Validate item object format:
      if (item.id && item.name && item.quantity) {
        item.quantity = validateNumber(item.quantity);
        item.dateAdded = Date.now();
        item.referralUrl = window.location.href;

        // We need this in order to validate the cart item for the current auth user later,
        // or to check if user has been logged out.
        item.authUserHash = window.authUserHash;

        const productItemProduct = new ProductCartItem(item);
        this.cart.items.push(productItemProduct);

        this.$emit('added-to-cart', item);

        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: 'addToCart',
          ecommerce: {
            currencyCode: 'NOK',
            add: {
              products: [
                productItemProduct.getDataLayerObject(),
              ],
            },
          },
        });

        return item;
      }

      Error('Invalid data passed to cart; property missing.');

      return false;
    },

    removeFromCart(index) {
      const collection = collect(this.cart.items);
      const itemRemoved = collection.splice(index, 1).first();
      this.cart.items = collection.all();
      this.$emit('removed-from-cart', itemRemoved.id);

      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'removeFromCart',
        ecommerce: {
          remove: {
            products: [
              (new ProductCartItem(itemRemoved)).getDataLayerObject(),
            ],
          },
        },
      });
    },

    clearCart() {
      this.cart = { items: [] };
      this.$emit('cleared-cart');
    },

    refreshCartItems() {
      return axios.post('/api/v1/cart/refresh-cart-items', this.cart.items)
        .then((response) => {
          const invalidItems = [];

          collect(response.data).each((freshItem) => {
            let originalItem = collect(this.cart.items).firstWhere('id', freshItem.id);

            if (originalItem) {
              originalItem = Object.assign(originalItem, freshItem);
              originalItem.dateAdded = Date.now();

              let originalItemHash = null;

              if (typeof originalItem.authUserHash !== 'undefined') {
                originalItemHash = originalItem.authUserHash;
              }

              if ((freshItem.authUserHash === false) ||
                  (freshItem.authUserHash !== null && originalItemHash !== window.authUserHash)
              ) {
                invalidItems.push(new ProductCartItem(originalItem));
              }
            }
          });

          return invalidItems;
        });
    },

    // Checkout methods

    getTotalWeight() {
      // Min. value of 1 gram if weight is not set
      return collect(this.cart.items).sum(item => (item.weight || 1) * item.quantity);
    },

    getOrderStatement() {
      const shippingOrderLine = collect(this.orderData.order_lines).firstWhere('type', 'shipping_fee');

      const shippingAmountWithoutVAT = typeof shippingOrderLine.total_amount !== 'undefined' &&
                                       typeof shippingOrderLine.total_tax_amount !== 'undefined' ?
        (shippingOrderLine.total_amount - shippingOrderLine.total_tax_amount) : null;

      const amountWithoutVAT = typeof this.orderData.order_amount !== 'undefined' &&
                               typeof this.orderData.order_tax_amount !== 'undefined' ?
        this.orderData.order_amount - this.orderData.order_tax_amount : null;

      // Divide price by 100 as Klarna stores prices in minor unit
      return {
        amountWithoutVAT: amountWithoutVAT === null ?
          null : amountWithoutVAT / 100,
        shippingAmount: shippingAmountWithoutVAT === null ?
          null : shippingAmountWithoutVAT / 100,
        taxAmount: typeof this.orderData.order_tax_amount === 'undefined' ?
          null : this.orderData.order_tax_amount / 100,
        totalAmount: typeof this.orderData.order_amount === 'undefined' ?
          null : this.orderData.order_amount / 100,
      };
    },

    // JS implementation of shouldAddVAT() in Helpers.php
    shouldAddVAT(price = null, forceAddVAT = null) {
      const cookieVar = cookie.read('showVAT');
      const priceVAT = price * 1.25;

      if (forceAddVAT !== null && price !== null) {
        return forceAddVAT ? priceVAT : price;
      }

      if (cookieVar === '0') {
        return price === null ? false : price;
      }

      if (cookieVar === '1') {
        return price === null ? true : priceVAT;
      }

      if (cookieVar === '' &&
        this.info.authUser &&
        (typeof this.info.authUser.displayVATPreference !== 'undefined' ||
          (this.info.authUser.customer &&
            typeof this.info.authUser.customer.display_vat !== 'undefined'))
      ) {
        let displayVAT = false;

        if (typeof this.info.authUser.displayVATPreference !== 'undefined') {
          displayVAT = this.info.authUser.displayVATPreference;
        } else {
          displayVAT = this.info.authUser.customer.display_vat;
        }

        if (price !== null && displayVAT) {
          return priceVAT;
        }

        return price || displayVAT;
      }

      return price === null ? true : priceVAT;
    },

    // Session methods

    addPaymentSession(sessionObject) {
      // Trigger deep watch
      this.cart = Object.assign({}, this.cart, {
        session: {
          id: sessionObject.session_id,
          clientToken: sessionObject.client_token,
          paymentMethodCategories: sessionObject.payment_method_categories,
          dateAdded: Date.now(),
          laravelToken: window.Laravel.csrfToken,
          authorizationToken: null, // Klarna auth token for finalization
        },
      });

      this.$emit('payment-session-updated', this.cart.session);
    },

    removePaymentSession() {
      this.cart.session = {};
      this.$emit('payment-session-updated', this.cart.session);
    },

    // Info methods

    addAuthUser(user) {
      // Clear any past sessions first, if any
      if (
        typeof this.cart.session === 'undefined' ||
        typeof this.cart.session.laravelToken === 'undefined' ||
        this.cart.session.laravelToken !== window.Laravel.csrfToken
      ) {
        this.removePaymentSession();
      }

      if (typeof user !== 'undefined') {
        this.info = {
          authUser: user,
        };

        this.$emit('added-auth-user', user);
      }
    },

    removeAuthUser() {
      this.info = {};
      this.$emit('removed-auth-user');
    },

    setPreferredShippingMethod(method) {
      this.cart.preferredShippingMethod = method;
      this.$emit('updated-preferred-shipping-method', method);
    },

    setPreferredPaymentMethod(method) {
      this.cart.preferredPaymentMethod = method;
      this.$emit('updated-preferred-payment-method', method);
    },

    // Helpers

    writeToLocalStorage() {
      // Write entire cart object to localStorage
      // NOTE: Only store non-sensitive data
      storage.setItem('cart', JSON.stringify(this.cart));
    },

    getLocalStorage() {
      const parsedCart = JSON.parse(storage.getItem('cart')) || { items: [] };
      parsedCart.items = collect(parsedCart.items).transform(item => new ProductCartItem(item)).all();

      return parsedCart;
    },
  },
});

export default store;
